import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { ArrowRight, Layers, Droplets, Sparkles, Boxes } from "lucide-react";
import api, { resolveImage } from "../lib/api";
import { Stars, VerifiedBadge } from "../components/Stars";

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

const HERO_IMAGE =
  "https://images.pexels.com/photos/27893067/pexels-photo-27893067.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=1920&fit=crop";

const features = [
  { icon: Layers, title: "60+ Wash", body: "Crack-resistant ink survives commercial laundering." },
  { icon: Droplets, title: "CMYK + White", body: "Full-color photo-realistic print, even on dark fabric." },
  { icon: Sparkles, title: "Soft Hand", body: "No plasticky feel. Reads like a screen print, fits like cotton." },
  { icon: Boxes, title: "From 1 piece", body: "Sample runs, capsules, or volume. No minimums." },
];

export default function Home() {
  const [items, setItems] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState({ average: 0, count: 0 });

  useEffect(() => {
    api.get("/gallery").then((r) => setItems(r.data.slice(0, 5))).catch(() => {});
    api.get("/feedback").then((r) => setReviews(r.data.slice(0, 3))).catch(() => {});
    api.get("/feedback/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden border-b border-black/10"
      >
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Stack of vibrant Xemi printed apparel"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-white/15 md:bg-gradient-to-r md:from-white md:via-white/40 md:via-40% md:to-transparent md:to-65%" />
        </div>
        <div className="grain absolute inset-0" />

        {/* Decorative logo watermark */}
        <img
          src="/logo.png"
          alt=""
          aria-hidden="true"
          width="640"
          height="640"
          loading="eager"
          decoding="sync"
          className="hidden md:block absolute right-6 lg:right-16 top-24 lg:top-32 w-[320px] lg:w-[460px] xl:w-[540px] opacity-100 drop-shadow-2xl pointer-events-none select-none"
        />

        <div className="relative px-6 md:px-12 lg:px-20 pt-28 pb-32 md:pt-40 md:pb-44">
          <motion.div variants={stagger} initial="hidden" animate="show" className="max-w-6xl">
            <motion.p
              variants={fadeUp}
              className="font-mono text-xs uppercase tracking-[0.3em] text-[#09090B]/70 mb-8"
            >
              Direct-to-Film + Sublimation — Cornwall, UK · Est. 2025
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-display font-black text-[14vw] md:text-[10vw] lg:text-[7.5rem] xl:text-[8.5rem] leading-[0.9] tracking-[-0.04em]"
            >
              Built to last,
              <br />
              <span className="relative inline-block">
                Designed to{" "}
                <span className="relative">
                  impress
                  <span className="absolute -bottom-2 left-0 right-0 h-3 bg-[#FFD600] -z-10" />
                </span>
              </span>
              <span className="text-[#E6007A]">.</span>
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="mt-10 max-w-xl font-mono text-base md:text-lg leading-relaxed text-[#27272A]"
            >
              We push pigment through film and onto fabric — and gas-bond dye into poly when the design calls for it. DTF + sublimation, no minimums, full-spectrum colour that holds its bite, wash after wash.
            </motion.p>
            <motion.div variants={fadeUp} className="mt-12 flex flex-wrap items-center gap-4">
              <Link
                to="/gallery"
                data-testid="hero-cta-gallery"
                className="btn-ink bg-black text-white px-8 py-5 text-xs font-mono uppercase tracking-[0.25em] inline-flex items-center gap-3"
              >
                See the work <ArrowRight size={16} />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-quote"
                className="btn-ink-yellow bg-white text-black border border-black px-8 py-5 text-xs font-mono uppercase tracking-[0.25em]"
              >
                Request a quote
              </Link>
            </motion.div>
          </motion.div>
        </div>

        {/* CMYK ribbon */}
        <div className="absolute bottom-0 left-0 right-0 h-2 flex">
          <div className="flex-1 bg-[#00E5FF]" />
          <div className="flex-1 bg-[#E6007A]" />
          <div className="flex-1 bg-[#FFD600]" />
          <div className="flex-1 bg-black" />
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-b border-black/10 overflow-hidden bg-black text-white py-6">
        <div className="flex marquee-track whitespace-nowrap font-display font-black text-3xl md:text-4xl uppercase tracking-tight">
          {Array.from({ length: 2 }).map((_, j) => (
            <div key={j} className="flex items-center gap-12 pr-12">
              {["T-Shirts", "•", "Hoodies", "•", "Caps", "•", "Tote Bags", "•", "Workwear", "•", "Custom Runs", "•"].map(
                (w, i) => (
                  <span key={`${j}-${i}`} className={w === "•" ? "text-[#FFD600]" : ""}>
                    {w}
                  </span>
                )
              )}
            </div>
          ))}
        </div>
      </section>

      {/* VIDEO SHOWCASE */}
      <section className="border-b border-black/10 bg-[#FAFAFA] px-6 md:px-12 lg:px-20 py-20 md:py-28">
        <div className="mb-12 grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
          <div className="md:col-span-7">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#71717A] mb-4">
              See it printed
            </p>
            <h2 className="font-display font-black text-5xl md:text-6xl leading-[0.9]">
              Your design
              <br />
              <span className="relative inline-block">
                in motion
                <span className="absolute -bottom-1 left-0 right-0 h-3 bg-[#00E5FF] -z-10" />
              </span>
              <span className="text-[#E6007A]">.</span>
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="font-mono text-base leading-relaxed text-[#27272A]">
              A quick look at how artwork lives on the press — applied to white and black tees and hoodies, built to last and designed to impress.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 max-w-5xl mx-auto">
          {[
            {
              src: "https://customer-assets.emergentagent.com/job_dtf-comments-gallery/artifacts/aes6w4wx_Firefly%20put%20the%20images%20on%20black%20and%20white%20t-sherds%20and%20hoodies%20and%20make%20presetation%20for%20custamer%20to%20.mp4",
              testid: "home-showcase-video-1",
              badge: "On the apparel",
            },
            {
              src: "https://customer-assets.emergentagent.com/job_dtf-comments-gallery/artifacts/5kfkdmr5_Untitled%289%29.mp4",
              testid: "home-showcase-video-2",
              badge: "Live from the press",
            },
          ].map((v) => (
            <div key={v.testid} className="relative border border-black/15 bg-black overflow-hidden shadow-2xl">
              <video
                data-testid={v.testid}
                src={v.src}
                controls
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                className="w-full h-auto block aspect-video object-cover"
              >
                Your browser does not support embedded video.
              </video>
              <div className="absolute top-3 left-3 flex items-center gap-2 bg-white/95 px-3 py-1.5">
                <span className="w-1.5 h-1.5 bg-[#E6007A] rounded-full animate-pulse" />
                <span className="font-mono text-[10px] uppercase tracking-[0.25em]">{v.badge}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES TEASER (BENTO) */}
      <section className="px-6 md:px-12 lg:px-20 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 mb-16 items-end">
          <div className="md:col-span-7">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#71717A] mb-4">
              01 / What we do
            </p>
            <h2 className="font-display font-black text-5xl md:text-7xl leading-[0.9]">
              Built for ink.
              <br />
              Engineered for fabric.
            </h2>
          </div>
          <div className="md:col-span-5 md:pb-2">
            <p className="font-mono text-base text-[#27272A] leading-relaxed">
              Four reasons brands, studios and indie labels keep coming back to our DTF press for their custom apparel runs.
            </p>
          </div>
        </div>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          {features.map((f, idx) => {
            const Icon = f.icon;
            const accents = ["#00E5FF", "#E6007A", "#FFD600", "#09090B"];
            return (
              <motion.div
                key={f.title}
                variants={fadeUp}
                data-testid={`feature-${idx}`}
                className="group p-8 border border-black/10 bg-white hover:border-black transition-colors"
              >
                <div
                  className="w-12 h-12 flex items-center justify-center mb-8"
                  style={{ backgroundColor: accents[idx] }}
                >
                  <Icon size={22} color={idx === 3 ? "#FFD600" : "#09090B"} />
                </div>
                <h3 className="font-display font-black text-2xl mb-3">{f.title}</h3>
                <p className="font-mono text-sm text-[#52525B] leading-relaxed">{f.body}</p>
              </motion.div>
            );
          })}
        </motion.div>
      </section>

      {/* GALLERY PREVIEW */}
      <section className="px-6 md:px-12 lg:px-20 pb-24 md:pb-32 border-t border-black/10 pt-24 md:pt-32 bg-white">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#71717A] mb-4">
              02 / Recent runs
            </p>
            <h2 className="font-display font-black text-5xl md:text-7xl leading-[0.9]">
              Off the press.
            </h2>
          </div>
          <Link
            to="/gallery"
            data-testid="home-gallery-link"
            className="font-mono text-xs uppercase tracking-[0.25em] inline-flex items-center gap-2 hover:text-[#E6007A] transition-colors"
          >
            View full gallery <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          {items[0] && (
            <Link
              to={`/gallery/${items[0].id}`}
              data-testid="home-gallery-0"
              className="md:col-span-7 row-span-2 group block overflow-hidden border border-black/10"
            >
              <div className="aspect-[4/5] overflow-hidden bg-[#F4F4F5]">
                <img
                  src={resolveImage(items[0].image_url)}
                  alt={items[0].title}
                  loading="eager"
                  decoding="async"
                  style={{ filter: "contrast(1.04) saturate(1.06)" }}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="p-5 flex items-center justify-between bg-white">
                <p className="font-display font-black text-xl">{items[0].title}</p>
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#71717A]">
                  {items[0].category}
                </span>
              </div>
            </Link>
          )}
          {items.slice(1, 5).map((it, i) => (
            <Link
              key={it.id}
              to={`/gallery/${it.id}`}
              data-testid={`home-gallery-${i + 1}`}
              className={`group block overflow-hidden border border-black/10 ${
                i < 2 ? "md:col-span-5" : "md:col-span-3"
              }`}
            >
              <div className={`overflow-hidden bg-[#F4F4F5] ${i < 2 ? "aspect-[16/10]" : "aspect-square"}`}>
                <img
                  src={resolveImage(it.image_url)}
                  alt={it.title}
                  loading="lazy"
                  decoding="async"
                  style={{ filter: "contrast(1.04) saturate(1.06)" }}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="p-4 bg-white">
                <p className="font-display font-black text-base truncate">{it.title}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEEDBACK PREVIEW */}
      <section
        data-testid="home-feedback-section"
        className="px-6 md:px-12 lg:px-20 py-24 md:py-32 border-t border-black/10 bg-[#FAFAFA]"
      >
        <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#71717A] mb-4">
              03 / What clients say
            </p>
            <h2 className="font-display font-black text-5xl md:text-7xl leading-[0.9]">
              Rated by you.
            </h2>
            {summary.count > 0 && (
              <div data-testid="home-feedback-summary" className="mt-6 flex items-center gap-4">
                <span className="font-display font-black text-4xl">{summary.average}</span>
                <Stars value={summary.average} size={22} testid="home-summary-stars" />
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#71717A]">
                  {summary.count} review{summary.count === 1 ? "" : "s"}
                </span>
              </div>
            )}
          </div>
          <Link
            to="/feedback"
            data-testid="home-feedback-link"
            className="font-mono text-xs uppercase tracking-[0.25em] inline-flex items-center gap-2 hover:text-[#E6007A] transition-colors"
          >
            Leave feedback <ArrowRight size={14} />
          </Link>
        </div>

        {reviews.length === 0 ? (
          <div className="border border-dashed border-black/15 bg-white p-10 text-center">
            <p className="font-mono text-sm text-[#71717A] mb-6">
              No reviews yet — tell us how we did.
            </p>
            <Link
              to="/feedback"
              data-testid="home-feedback-cta-empty"
              className="btn-ink inline-block bg-black text-white px-7 py-4 font-mono text-xs uppercase tracking-[0.25em]"
            >
              Be the first →
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {reviews.map((r) => (
              <div
                key={r.id}
                data-testid={`home-feedback-${r.id}`}
                className="border border-black/10 bg-white p-7 hover:border-black transition-colors"
              >
                <Stars value={r.rating} size={18} testid={`home-feedback-stars-${r.id}`} />
                <p className="font-mono text-sm leading-relaxed text-[#27272A] mt-5 line-clamp-4">
                  {r.message}
                </p>
                <p className="font-display font-black text-base mt-5 flex items-center gap-2 flex-wrap">
                  {r.name} {r.verified_buyer && <VerifiedBadge />}
                </p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">
                  {new Date(r.created_at).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="px-6 md:px-12 lg:px-20 py-24 md:py-32 bg-black text-white">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 mb-6">
              04 / Next drop
            </p>
            <h2 className="font-display font-black text-6xl md:text-8xl leading-[0.85]">
              Send us your<br />
              <span className="text-[#00E5FF]">artwork.</span>
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="font-mono text-base leading-relaxed text-white/80 mb-8">
              We'll print a single sample on us. No commitment, no upsell. If you love it, we run the full batch.
            </p>
            <Link
              to="/contact"
              data-testid="home-cta-bottom"
              className="inline-block bg-white text-black px-7 py-4 font-mono text-xs uppercase tracking-[0.25em] btn-ink-magenta"
            >
              Send a file →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
